import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { com } from "../../proto/game.pb";
import { useNotification, useWebSocket } from "../../shared/contexts";
import { MT } from "../../shared/types/proto/mt";
import NotificationCashout from "../../shared/ui/Notification/NotificationComponent/NotificationCashout";
import { useUserStore } from "../../store/userStore";

export type AutoCashoutUIState = {
  enabled: boolean;
  value: string | number | null;
  lastValid: number;
};

export type PlaceBetParams = {
  amount: number;
  slotId?: number;
  roundId?: number;
};

export type CashOutParams = {
  slotId?: number;
  roundId?: number;
};

export type CancelBetParams = {
  slotId?: number;
  roundId?: number;
};

type CashOutListener = (res: com.achlid.crash.game.ICashOutResult) => void;
type BetResultListener = (res: com.achlid.crash.game.IBetResult) => void;
type RoundCrashListener = (rc: com.achlid.crash.game.IRoundCrash) => void;
type CancelBetListener = (res: com.achlid.crash.game.ICancelBetResult) => void;

export function useBetting(
  defaultSlotId: number,
  handlers?: {
    onCashOut?: CashOutListener;
    onBetResult?: BetResultListener;
    onRoundCrash?: RoundCrashListener;
    onCancelBet?: CancelBetListener;
    onFreebetUsed?: (freebetId: string) => void;
  }
) {
  const { sendProto, onMessage } = useWebSocket();
  const { showError, showSuccess, showInfo } = useNotification();
  const [autoCashout, setAutoCashout] = useState<AutoCashoutUIState>({
    enabled: false,
    value: 1.1,
    lastValid: 1.1,
  });
  const [hasActiveBet, setHasActiveBet] = useState(false);
  const { updateProfile } = useUserStore();
  // Идемпотентный кошелёк: успешный кэшаут может прийти в два шага —
  // промежуточный (wallet_pending=true) и финальный (wallet_pending=false).
  // Финальный выигрыш (WIN-баннер) показываем только один раз на операцию,
  // ключ — wallet_operation_id, с фолбэком на (round_id, slot_id).
  const finalizedCashOutsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const offBetResult = onMessage(MT.BET_RESULT, (msg) => {
      const res = msg.betResult;

      if (res?.slotId !== defaultSlotId) return;
      if (res.success) {
        setHasActiveBet(true);
        // Показываем информацию о фрибете если он был использован
        console.log(res, "betResult");
        if (res.isFreebet) {
          showInfo(
            `✨ Ставка сделана за счёт фрибета на $${res.amount?.toFixed(2)}`
          );
        }
      } else {
        setHasActiveBet(false);
        showError(res.errorMessage || "Bet failed");
      }
      handlers?.onBetResult?.(res);
    });

    const offCancelBetResult = onMessage(MT.CANCEL_BET_RESULT, (msg) => {
      const res = msg.cancelBetResult;
      if (res?.slotId !== defaultSlotId) return;
      if (res?.success) {
        setHasActiveBet(false);
        handlers?.onCancelBet?.(res);
      } else {
        showError(res?.errorMessage || "Cancel bet failed");
      }
    });
    const offCashOutResult = onMessage(MT.CASH_OUT_RESULT, (msg) => {
      const res = msg.cashOutResult;
      if (res?.slotId !== defaultSlotId) return;

      // Идемпотентный кошелёк: отсутствие поля трактуем как wallet_pending=false
      const walletPending = res.walletPending === true;

      if (res.success && walletPending) {
        updateProfile({ balance: res.balance });
        handlers?.onCashOut?.(res);
        return;
      }

      if (res.success) {
        // Финальный успех (wallet_pending=false) — фиксируем выигрыш.
        // Дубль финального результата не должен повторно показывать баннер:
        // связываем по wallet_operation_id, фолбэк — (round_id, slot_id).
        const opKey =
          res.walletOperationId ||
          `${String(res.roundId ?? "")}:${res.slotId ?? defaultSlotId}`;
        if (finalizedCashOutsRef.current.has(opKey)) {
          handlers?.onCashOut?.(res);
          return;
        }
        finalizedCashOutsRef.current.add(opKey);

        setHasActiveBet(false);
        const payoutStr = res.payout?.toFixed(2) ?? res.payout;
        const multStr = res.multiplier?.toFixed(2) ?? res.multiplier;

        updateProfile({
          balance: res.balance,
        });

        // Для фрибета payout содержит только прибыль
        if (res.isFreebet) {
          showInfo(`✨ Фрибет кэшаут: прибыль $${payoutStr} на ${multStr}x`);
          // Фрибет будет удален сервером из списка активных
        } else {
          showSuccess(
            <NotificationCashout
              payout={payoutStr as string}
              multiplayer={multStr as string}
            />,
            undefined,
            (res.slotId as 1 | 2) ?? (defaultSlotId as 1 | 2)
          );
        }
      } else {
        // success=false — финальная ошибка (в т.ч. fail-safe success=false + wallet_pending=true)
        showError(res.errorMessage || "Cash out failed");
      }
      handlers?.onCashOut?.(res);
    });
    const offRoundCrash = onMessage(MT.ROUND_CRASH, (msg) => {
      const rc = msg.roundCrash;
      if (!rc) return;
      setHasActiveBet(false);
      // Новый раунд — сбрасываем набор финализированных кэшаутов
      finalizedCashOutsRef.current.clear();
      handlers?.onRoundCrash?.(rc);
    });

    return () => {
      offBetResult();
      offCancelBetResult();
      offCashOutResult();
      offRoundCrash();
    };
  }, [
    onMessage,
    defaultSlotId,
    handlers?.onBetResult,
    handlers?.onCashOut,
    handlers?.onRoundCrash,
    handlers?.onCancelBet,
    showError,
    showInfo,
    showSuccess,
    updateProfile,
  ]);

  useEffect(() => {
    const off = onMessage(MT.AUTO_CASH_OUT_RESULT, (msg) => {
      const res = msg.autoCashOutResult;
      if (res?.slotId !== defaultSlotId) return;
      if (!res?.success) {
        showError(res?.errorMessage || "Failed to update auto cashout");
        return;
      }
      const enabled = !!(res.level && res.level > 0);
      const levelNum = res.level != null ? Number(res.level) : null;
      setAutoCashout((prev) => ({
        enabled,
        value: enabled && levelNum != null ? levelNum : prev.lastValid,
        lastValid: enabled && levelNum != null ? levelNum : prev.lastValid,
      }));
    });
    return () => off();
  }, [onMessage, showError, defaultSlotId]);

  const api = useMemo(() => {
    return {
      placeBet: ({ amount, slotId, roundId }: PlaceBetParams) => {
        const payload = {
          type: MT.PLACE_BET,
          placeBet: {
            slotId: slotId ?? defaultSlotId ?? 0,
            amount,
            roundId,
          },
        };
        setHasActiveBet(true);
        sendProto(payload);
      },
      cashOut: ({ slotId, roundId }: CashOutParams = {}) => {
        const payload = {
          type: MT.CASH_OUT,
          cashOut: {
            slotId: slotId ?? defaultSlotId ?? 0,
            roundId,
          },
        };

        sendProto(payload);
      },
      cancelBet: ({ slotId, roundId }: CancelBetParams = {}) => {
        const payload = {
          type: MT.CANCEL_BET,
          cancelBet: {
            slotId: slotId ?? defaultSlotId ?? 0,
            roundId,
          },
        };
        sendProto(payload);
      },
      startAutoPlay: (params: {
        slotId?: number;
        betAmount: number;
        rounds?: number;
        stopOnLoss?: number;
        stopOnProfit?: number;
        stopOnSingleWin?: number;
      }) => {
        const payload = {
          type: MT.AUTO_PLAY,
          autoPlay: {
            slotId: params.slotId ?? defaultSlotId ?? 0,
            betAmount: params.betAmount,
            rounds: params.rounds ?? 0,
            stopOnLoss: params.stopOnLoss,
            stopOnProfit: params.stopOnProfit,
            stopOnSingleWin: params.stopOnSingleWin,
          },
        };
        sendProto(payload);
      },
      stopAutoPlay: (slotId?: number) => {
        const payload = {
          type: MT.STOP_AUTO_PLAY,
          stopAutoPlay: {
            slotId: slotId ?? defaultSlotId ?? 0,
          },
        };
        sendProto(payload);
      },
      setAutoCashOut: (level?: number, slotId?: number) => {
        const payload = {
          type: MT.SET_AUTO_CASH_OUT,
          setAutoCashOut: {
            slotId: slotId ?? defaultSlotId ?? 0,
            level: level,
          },
        };
        sendProto(payload);
      },
      hasActiveBet,

      updateAutoCashoutUI: (patch: Partial<AutoCashoutUIState>) => {
        setAutoCashout((prev) => ({ ...prev, ...patch }));
      },
      autoCashout,
    };
  }, [sendProto, showError, autoCashout]);

  return api;
}
